import React, { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Check, RotateCcw, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STORAGE_KEY = "kilolava.nav_order.admin";

function readOrder() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeOrder(paths) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(paths)); } catch { /* ignore */ }
}
function clearOrder() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/** Ordina `items` in base a `paths`; gli item non presenti in `paths` finiscono in fondo. */
export function applyOrder(items, paths) {
  if (!paths || !paths.length) return items;
  const byPath = new Map(items.map((it) => [it.to, it]));
  const ordered = [];
  const seen = new Set();
  for (const p of paths) {
    const it = byPath.get(p);
    if (it) { ordered.push(it); seen.add(it.to); }
  }
  for (const it of items) if (!seen.has(it.to)) ordered.push(it);
  return ordered;
}

function SortableItem({ item }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.to });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-3 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm font-medium text-gray-700"
      data-testid={`nav-reorder-item-${item.testId || item.to}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="p-1 -m-1 cursor-grab touch-none text-gray-400 hover:text-gray-700 shrink-0"
        aria-label={`Trascina ${item.label}`}
        data-testid={`nav-reorder-drag-${item.testId || item.to}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <item.icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </div>
  );
}

/**
 * SortableNav — renderizza la nav in modalità normale (NavLink) o riordino (drag&drop).
 * Solo se `canReorder` è true mostra il pulsante «Riordina» in alto.
 */
const KEYWORDS = {
  "/admin": ["home", "dashboard", "riepilogo"],
  "/admin/fatturazione": ["fattura", "fatture", "billing", "prezzi", "addebiti", "recap", "correzioni"],
  "/admin/prenotazioni": ["booking", "check-in", "check-out", "excel", "import", "calendario", "ospiti", "collega case", "alias"],
  "/admin/storni-dipendenti": ["storni", "non conformi", "percentuale", "qualità", "biancheria rovinata"],
  "/admin/ore-dipendenti": ["ore", "tempi", "pdf", "buste paga", "minuti"],
  "/admin/cestino": ["eliminati", "ripristina", "trash"],
  "/admin/structures": ["case", "appartamenti", "letti", "orari check-in", "nomi alternativi", "alias", "import excel"],
  "/admin/setup": ["dati mancanti", "setup da completare", "controlli"],
  "/logistica": ["consegne", "biancheria", "kg", "ritiro"],
  "/admin/lavanderia": ["pesata", "foto", "privata", "chili"],
};

function matchesQuery(item, q) {
  if (!q) return true;
  const hay = [item.label, item.to, ...(KEYWORDS[item.to] || [])].join(" ").toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

export function SortableNav({ items, canReorder, isMobile, onNavigate }) {
  const [savedPaths, setSavedPaths] = useState(readOrder());
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const orderedItems = useMemo(() => applyOrder(items, savedPaths).filter((i) => matchesQuery(i, query)), [items, savedPaths, query]);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(orderedItems.map((i) => i.to));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const enterEdit = () => {
    setDraft(orderedItems.map((i) => i.to));
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);
  const saveEdit = () => {
    writeOrder(draft);
    setSavedPaths(draft);
    setEditing(false);
    toast.success("Ordine menu salvato su questo dispositivo");
  };
  const resetOrder = () => {
    clearOrder();
    setSavedPaths(null);
    setDraft(items.map((i) => i.to));
    setEditing(false);
    toast.success("Ordine ripristinato");
  };

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = draft.indexOf(active.id);
    const newIdx = draft.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    setDraft(arrayMove(draft, oldIdx, newIdx));
  };

  const draftItems = useMemo(
    () => draft.map((p) => items.find((i) => i.to === p)).filter(Boolean),
    [draft, items],
  );

  return (
    <nav className={`flex-1 ${isMobile ? "p-3" : "p-4"} space-y-1 overflow-y-auto`}>
      <div className="relative mb-2">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && orderedItems.length && !editing) { navigate(orderedItems[0].to); setQuery(""); onNavigate?.(); } if (e.key === "Escape") setQuery(""); }}
          placeholder="Cerca sezione…"
          className="w-full h-10 rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC4E5B]/30 focus:bg-white"
          data-testid={isMobile ? "nav-search-mobile" : "nav-search"}
        />
        {query && (
          <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200" data-testid="nav-search-clear">
            <X className="w-3.5 h-3.5 text-gray-500" />
          </button>
        )}
      </div>
      {query && orderedItems.length === 0 && (
        <p className="text-xs text-gray-400 px-3 py-2" data-testid="nav-search-empty">Nessuna sezione trovata</p>
      )}
      {canReorder && !editing && !query && (
        <button
          type="button"
          onClick={enterEdit}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-[#EC4E5B] hover:bg-[#EC4E5B]/5 border border-dashed border-gray-200 hover:border-[#EC4E5B]/40 transition"
          data-testid="nav-reorder-enter"
        >
          <Pencil className="w-3.5 h-3.5" /> Riordina menu
        </button>
      )}
      {canReorder && editing && (
        <div className="mb-2 flex items-center gap-2">
          <Button
            size="sm"
            onClick={saveEdit}
            className="flex-1 h-9 rounded-xl bg-[#EC4E5B] hover:bg-[#d94654] text-white"
            data-testid="nav-reorder-save"
          >
            <Check className="w-4 h-4 mr-1" /> Salva ordine
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={cancelEdit}
            className="h-9 rounded-xl"
            data-testid="nav-reorder-cancel"
          >
            Annulla
          </Button>
        </div>
      )}
      {canReorder && editing && savedPaths && (
        <button
          type="button"
          onClick={resetOrder}
          className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-700 mb-1"
          data-testid="nav-reorder-reset"
        >
          <RotateCcw className="w-3 h-3" /> Ripristina ordine originale
        </button>
      )}
      {editing ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={draft} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5" data-testid="nav-reorder-list">
              {draftItems.map((n) => <SortableItem key={n.to} item={n} />)}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        orderedItems.map((n) => (
          <NavLink
            key={n.testId || n.to}
            to={n.to}
            end={n.to === "/admin"}
            data-testid={isMobile ? `${n.testId}-mobile` : n.testId}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all ${
                isMobile ? "min-h-[48px]" : ""
              } ${
                isActive
                  ? "bg-[#EC4E5B] text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-100"
              }`
            }
          >
            <n.icon className="w-4 h-4" />
            <span>{n.label}</span>
          </NavLink>
        ))
      )}
    </nav>
  );
}
